import { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, Users, Truck, Building, ArrowUpRight, ArrowDownRight, Wallet, BarChart3, Clock, History, PieChart } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { EarningsAPI, EarningsDashboard } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';
import { useNavigate } from 'react-router-dom';
import { MoneyFlowDiagram } from '../../components/earnings/MoneyFlowDiagram';
import { EarningsPieChart } from '../../components/earnings/EarningsChart';
import { AnimatedKPICard } from '../../components/earnings/AnimatedKPICard';
import { WithdrawalAlertsBar } from '../../components/earnings/WithdrawalAlertsBar';
import { FinancialHealthMeter } from '../../components/earnings/FinancialHealthMeter';

export default function EarningsDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<EarningsDashboard | null>(null);
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('monthly');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await EarningsAPI.getDashboard({ period });
      if (response.success) {
        setDashboard(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching earnings dashboard:', err);
      setError(err.message || 'Failed to load earnings dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading earnings dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load dashboard'}</p>
          <Button onClick={fetchDashboard}>Retry</Button>
        </div>
      </div>
    );
  }

  const { platformEarnings, earningsBreakdown, recentEarnings } = dashboard;

  return (
    <div className="space-y-4 sm:space-y-6 px-1 sm:px-0">
      {/* Withdrawal Alerts Bar */}
      <WithdrawalAlertsBar onRefresh={fetchDashboard} />

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Earnings & Payments</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Monitor platform earnings and manage payouts</p>
        </div>
        <div className="flex gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
            className="px-4 py-2 border rounded-md bg-background"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>

      {/* Platform Earnings Overview - Animated KPI Cards */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
        <AnimatedKPICard
          title="Total Earnings"
          value={platformEarnings.totalEarnings}
          format="currency"
          icon={<DollarSign className="h-5 w-5" />}
          colorScheme="success"
          subtitle={`${platformEarnings.totalTransactions} transactions`}
          trendData={recentEarnings.slice(-7).map(e => e.platformEarnings)}
        />

        <AnimatedKPICard
          title="Paid Earnings"
          value={platformEarnings.paidEarnings}
          format="currency"
          icon={<TrendingUp className="h-5 w-5" />}
          colorScheme="success"
          subtitle={`${platformEarnings.totalEarnings > 0 ? ((platformEarnings.paidEarnings / platformEarnings.totalEarnings) * 100).toFixed(1) : 0}% of total`}
        />

        <AnimatedKPICard
          title="Pending Earnings"
          value={platformEarnings.pendingEarnings}
          format="currency"
          icon={<Clock className="h-5 w-5" />}
          colorScheme="warning"
          subtitle="Awaiting settlement"
        />

        <AnimatedKPICard
          title="Avg per Transaction"
          value={platformEarnings.totalTransactions > 0 ? platformEarnings.totalEarnings / platformEarnings.totalTransactions : 0}
          format="currency"
          icon={<BarChart3 className="h-5 w-5" />}
          colorScheme="blue"
          subtitle="Platform commission"
        />
      </div>

      {/* Earnings Breakdown by Entity */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Supplier Earnings
            </CardTitle>
            <CardDescription>Total payments to suppliers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold">{formatCurrency(earningsBreakdown?.supplier?.totalEarnings || 0)}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Paid</p>
                  <p className="text-lg font-semibold text-green-600">
                    {formatCurrency(earningsBreakdown?.supplier?.paidEarnings || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pending</p>
                  <p className="text-lg font-semibold text-orange-600">
                    {formatCurrency(earningsBreakdown?.supplier?.pendingEarnings || 0)}
                  </p>
                </div>
              </div>
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  {earningsBreakdown?.supplier?.totalTransactions || 0} transactions
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/earnings/pending-payouts?type=supplier')}
              >
                View Pending Payouts
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Transporter Earnings
            </CardTitle>
            <CardDescription>Total payments to transporters</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold">{formatCurrency(earningsBreakdown?.transporter?.totalEarnings || 0)}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Paid</p>
                  <p className="text-lg font-semibold text-green-600">
                    {formatCurrency(earningsBreakdown?.transporter?.paidEarnings || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pending</p>
                  <p className="text-lg font-semibold text-orange-600">
                    {formatCurrency(earningsBreakdown?.transporter?.pendingEarnings || 0)}
                  </p>
                </div>
              </div>
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  {earningsBreakdown?.transporter?.totalTransactions || 0} transactions
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/earnings/pending-payouts?type=transporter')}
              >
                View Pending Payouts
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Platform Earnings
            </CardTitle>
            <CardDescription>Net platform commission</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold">{formatCurrency(earningsBreakdown?.admin?.totalEarnings || 0)}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Paid</p>
                  <p className="text-lg font-semibold text-green-600">
                    {formatCurrency(earningsBreakdown?.admin?.paidEarnings || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pending</p>
                  <p className="text-lg font-semibold text-orange-600">
                    {formatCurrency(earningsBreakdown?.admin?.pendingEarnings || 0)}
                  </p>
                </div>
              </div>
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  {earningsBreakdown?.admin?.totalTransactions || 0} transactions
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/earnings/statistics')}
              >
                View Statistics
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visualizations Row */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {/* Money Flow Diagram */}
        <MoneyFlowDiagram
          data={{
            totalOrderValue: recentEarnings.reduce((sum, e) => sum + e.totalOrderValue, 0) || 100000,
            supplierPayments: recentEarnings.reduce((sum, e) => sum + e.supplierPayments, 0) || 85000,
            transporterPayments: recentEarnings.reduce((sum, e) => sum + e.transporterPayments, 0) || 10000,
            platformEarnings: recentEarnings.reduce((sum, e) => sum + e.platformEarnings, 0) || 5000,
          }}
          className="lg:col-span-1"
        />

        {/* Financial Health Meter */}
        <FinancialHealthMeter
          completedAmount={platformEarnings.paidEarnings}
          pendingAmount={platformEarnings.pendingEarnings}
          processingAmount={0}
          title="Payout Health"
          description="Completed vs pending payouts"
        />

        {/* Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Earnings Distribution
            </CardTitle>
            <CardDescription>
              Breakdown by entity type (cumulative)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <EarningsPieChart
              data={[
                { name: 'Supplier', value: earningsBreakdown?.supplier?.totalEarnings || 0 },
                { name: 'Transporter', value: earningsBreakdown?.transporter?.totalEarnings || 0 },
                { name: 'Platform', value: earningsBreakdown?.admin?.totalEarnings || 0 },
              ]}
              height={280}
            />
          </CardContent>
        </Card>
      </div>


      {/* Recent Earnings */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Recent Earnings</CardTitle>
              <CardDescription>Latest earnings transactions</CardDescription>
            </div>
            <Button variant="outline" onClick={() => navigate('/earnings/history')}>
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentEarnings.length > 0 ? (
              recentEarnings.slice(0, 10).map((earning) => (
                <div key={earning._id} className="flex items-center justify-between border-b pb-3">
                  <div className="flex-1">
                    <p className="font-medium">{earning.orderNumber}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(earning.earnedAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(earning.totalOrderValue)}</p>
                    <div className="flex gap-2 text-xs text-muted-foreground">
                      <span>S: {formatCurrency(earning.supplierPayments)}</span>
                      <span>T: {formatCurrency(earning.transporterPayments)}</span>
                      <span className="text-green-600 font-medium">
                        P: {formatCurrency(earning.platformEarnings)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No recent earnings found
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-3">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/earnings/pending-payouts')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <ArrowDownRight className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold">Pending Payouts</p>
                <p className="text-sm text-muted-foreground">Process supplier & transporter payments</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/earnings/history')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold">Earnings History</p>
                <p className="text-sm text-muted-foreground">View all earnings transactions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/earnings/statistics')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="font-semibold">Statistics</p>
                <p className="text-sm text-muted-foreground">Detailed earnings analytics</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-primary/50 bg-gradient-to-br from-primary/5 to-transparent" onClick={() => navigate('/earnings/withdrawals')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Wallet className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold">Withdrawal Requests</p>
                <p className="text-sm text-muted-foreground">Manage approval & settlements</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div >
  );
}
